import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Table, 
  Button, 
  Input, 
  Select, 
  Space, 
  Typography,
  Row,
  Col,
  Tag,
  message
} from 'antd';
import type { TablePaginationConfig } from 'antd/es/table';
import { 
  PlusOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import AdminLayout from '@/components/Layouts/AdminLayout/AdminLayout';
import { adminTrophyBadgeApi } from '@/services/api';

const { Title } = Typography;
const { Option } = Select;
const DEFAULT_PAGE_SIZE = 10;

interface TrophyBadgeData {
  id: number;
  trophyBadgeId: number;
  trophyBadgeName: string;
  type: 'trophy' | 'badge';
  studentUserId: number;
  studentName: string;
  awardedAt: string;
  reason?: string;
  grantedById?: number;
  grantedByName?: string;
}

const AdminTrophyBadgeList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialType = searchParams.get('type') || 'all';
  const initialKeyword = searchParams.get('q') || '';
  const initialPage = Number(searchParams.get('page')) || 1;
  const initialPageSize = Number(searchParams.get('size')) || DEFAULT_PAGE_SIZE;
  const [searchType, setSearchType] = useState<string>(initialType);
  const [searchKeyword, setSearchKeyword] = useState<string>(initialKeyword);
  const [loading, setLoading] = useState(false);
  const [trophyBadges, setTrophyBadges] = useState<TrophyBadgeData[]>([]);
  const [page, setPage] = useState<number>(initialPage);
  const [pageSize, setPageSize] = useState<number>(initialPageSize);
  const [total, setTotal] = useState<number>(0);

  const typeOptions = [
    { value: 'all', label: '전체' },
    { value: 'trophy', label: '트로피' },
    { value: 'badge', label: '뱃지' },
  ];

  const syncSearchParams = useCallback((nextType: string, nextPage: number, nextSize: number, keyword: string) => {
    const params = new URLSearchParams();
    if (nextType && nextType !== 'all') params.set('type', nextType);
    if (keyword) params.set('q', keyword);
    if (nextPage > 1) params.set('page', String(nextPage));
    if (nextSize !== DEFAULT_PAGE_SIZE) params.set('size', String(nextSize));
    setSearchParams(params);
  }, [setSearchParams]);

  const fetchData = useCallback(async (typeValue = searchType, nextPage = page, nextPageSize = pageSize) => {
    setLoading(true);
    try {
      const response = await adminTrophyBadgeApi.listAwards({
        type: typeValue && typeValue !== 'all' ? typeValue : undefined,
        page: nextPage - 1,
        size: nextPageSize,
      });
      const payload = response?.data || response;
      const pageData = payload?.content ? payload : payload?.data?.content ? payload.data : payload?.data?.data?.content ? payload.data.data : payload;
      const content = Array.isArray(pageData?.content) ? pageData.content : [];
      const totalElements = pageData?.totalElements ?? pageData?.total ?? payload?.totalElements ?? content.length;
      const baseIndex = (nextPage - 1) * nextPageSize;
      const numbered = content.map((item: any, idx: number) => ({
        ...item,
        rowNo: baseIndex + idx + 1,
      }));
      setTrophyBadges(numbered);
      setPage(nextPage);
      setPageSize(nextPageSize);
      setTotal(totalElements);
    } catch (error) {
      message.error('데이터를 불러오는데 실패했습니다.');
      console.error(error);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [searchType, page, pageSize]);

  useEffect(() => {
    fetchData(searchType, page, pageSize);
  }, [searchType, page, pageSize, fetchData]);

  useEffect(() => {
    syncSearchParams(searchType, page, pageSize, searchKeyword);
  }, [searchType, page, pageSize, searchKeyword, syncSearchParams]);

  const handleAddTrophyBadge = () => {
    navigate('/admin/trophy-badge/add');
  };

  const handleEditTrophyBadge = (id: number) => {
    navigate(`/admin/trophy-badge/detail/${id}`, { state: { from: `${location.pathname}${location.search}` } });
  };

  const handleTypeChange = (value: string) => {
    setSearchType(value);
    setPage(1);
  };

  const filteredTrophyBadges = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    if (!keyword) return trophyBadges;
    return trophyBadges.filter((item) => {
      const studentMatch = item.studentName?.toLowerCase().includes(keyword);
      const grantedByMatch = item.grantedByName?.toLowerCase().includes(keyword);
      return studentMatch || grantedByMatch;
    });
  }, [trophyBadges, searchKeyword]);

  const paginationConfig: TablePaginationConfig = {
    current: page,
    pageSize,
    total,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (totalCount) => {
      const start = Math.max(totalCount - (page * pageSize) + 1, 1);
      const end = Math.max(totalCount - ((page - 1) * pageSize), 1);
      return `${start}-${end} / 총 ${totalCount}개`;
    },
  };

  const columns = [
    {
      title: '순번',
      dataIndex: 'id',
      key: 'id',
      render: (_: any, record: any, index: number) => {
        const base = (page - 1) * pageSize;
        const totalCount = total || filteredTrophyBadges.length || trophyBadges.length;
        const computed = totalCount > 0 ? totalCount - (base + index) : filteredTrophyBadges.length - index;
        return computed > 0 ? computed : 0;
      },
      width: 80,
      align: 'center' as const
    },
    {
      title: '유형',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      align: 'center' as const,
      render: (type: string) => (
        <Tag color={type === 'trophy' ? 'gold' : 'blue'}>
          {type === 'trophy' ? '트로피' : '뱃지'}
        </Tag>
      )
    },
    {
      title: '트로피/뱃지 이름',
      dataIndex: 'trophyBadgeName',
      key: 'trophyBadgeName',
      width: 180
    },
    {
      title: '수여대상자',
      dataIndex: 'studentName',
      key: 'studentName',
      width: 150
    },
    {
      title: '수여자',
      dataIndex: 'grantedByName',
      key: 'grantedByName',
      width: 120,
      render: (name: string) => name || '-'
    },
    {
      title: '수여일',
      dataIndex: 'awardedAt',
      key: 'awardedAt',
      width: 150,
      align: 'center' as const
    },
  ];

  return (
    <AdminLayout>
      <div style={{ padding: '24px' }}>
        {/* 헤더 */}
        <div style={{ marginBottom: 24 }}>
          <Title level={2}>트로피 및 뱃지 수여 관리</Title>
        </div>

        {/* 필터 및 액션 영역 */}
        <Row gutter={16} align="bottom" className='mb-4'>
          <Col span={6}>
            <label className="block mb-2 text-sm font-medium">유형</label>
            <Select
              placeholder="유형을 선택하세요"
              value={searchType}
              onChange={handleTypeChange}
              style={{ width: '100%' }}
              size="large"
            >
              {typeOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={10}>
            <label className="block mb-2 text-sm font-medium">수여자/수여 대상자 검색</label>
            <Input
              placeholder="이름을 입력하세요"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              allowClear
              size="large"
            />
          </Col>
          <Col span={8} style={{ textAlign: 'right' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddTrophyBadge}
              size="large"
            >
              트로피/뱃지 수여
            </Button>
          </Col>
        </Row>

        {/* 테이블 */}
        <Table
          columns={columns}
          dataSource={filteredTrophyBadges}
          rowKey="id"
          loading={loading}
          onRow={(record) => ({
            onClick: () => handleEditTrophyBadge(record.id),
            style: { cursor: 'pointer' }
          })}
          pagination={paginationConfig}
          onChange={(pager) => {
            const current = pager.current || 1;
            const size = pager.pageSize || pageSize;
            setPage(current);
            setPageSize(size);
          }}
        />
      </div>
    </AdminLayout>
  );
};

export default AdminTrophyBadgeList; 